import {
  ChangeEvent,
  ChangeEventHandler,
  ComponentProps,
  useCallback,
  useEffect,
  useState,
} from 'react';
import locale from '@glu/locale';
import { Select } from '@glu/form-components';
import { IFilterProps, IFilterable, ISelectFilter } from '../Filters/Filters';

type TSelectProps = ComponentProps<typeof Select>;
type TOptionType = TSelectProps['options'];

export interface ISelectFilterFilterable extends IFilterable {
  /** The list of options avaliable in the select dropdown */
  filterOptions: IFilterable['filterOptions'] &
    Partial<TSelectProps> & {
      getOptions(filterable: ISelectFilterFilterable): Promise<TOptionType>;
    };
}

export interface ISelectFilterProps extends IFilterProps {
  /** The current value of the filter. Since state is not managed
   * locally this is required to display any current filter values.
   */
  value?: ISelectFilter;
  /** Updated filterable element with options required for select */
  filterable: ISelectFilterFilterable;
}

export const defaultParseSelectFilterValue = ({
  event,
  filterable,
  options,
}: {
  filterable: IFilterable;
  event: ChangeEvent<HTMLSelectElement>;
  options: TOptionType;
}) => {
  const filter = event.target.value;
  const selectedOption = options.find((o) => o.value === filter);
  return {
    filterable,
    value: { filter, label: selectedOption?.label || filter, type: 'equals' },
  };
};

const SelectFilter = ({ filterable, onChange, value }: ISelectFilterProps) => {
  const [options, setOptions] = useState<TOptionType>([]);
  const { field, filterOptions, headerName } = filterable;

  const handleChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    (event) => {
      const changeValue = (filterOptions.parseFilterValue || defaultParseSelectFilterValue)({
        filterable,
        event,
        options,
      });
      onChange(changeValue);
    },
    [filterOptions.parseFilterValue, filterable, onChange, options]
  );

  useEffect(() => {
    /** TODO: need to handle error case. v13 also didn't handle an error case here */
    filterOptions.getOptions(filterable).then((resolvedOptions) => setOptions(resolvedOptions));
  }, [filterOptions, filterable]);

  return (
    <Select
      disableInputAPI
      labelText={`${headerName} ${locale.get('dataComponents.filter')}`}
      name={field}
      onChange={handleChange}
      options={options}
      placeholderText={locale.get('dataComponents.select')}
      screenReaderLabel
      value={value?.filter}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...filterOptions.componentProps}
    />
  );
};

export default SelectFilter;
